import { useState, RefObject } from "react";
import html2pdf from "html2pdf.js";
import { toast } from "@/hooks/use-toast";

const prepareSVGsForPDF = (element: HTMLElement) => {
  const svgElements = element.querySelectorAll('svg');
  console.log(`[PDF] Preparando ${svgElements.length} SVGs para exportação`);
  
  svgElements.forEach((svg, index) => {
    const rect = svg.getBoundingClientRect();
    console.log(`[PDF] SVG ${index}: width=${rect.width}, height=${rect.height}`);
    
    // Se dimensões são zero, usar dimensões padrão
    const width = rect.width > 0 ? rect.width : 800;
    const height = rect.height > 0 ? rect.height : 400;
    
    svg.setAttribute('width', width.toString());
    svg.setAttribute('height', height.toString());
    svg.style.width = `${width}px`;
    svg.style.height = `${height}px`;
  });
};

export function useExportToPDF(elementRef: RefObject<HTMLElement>, defaultFilename = "export.pdf") {
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async (customFilename?: string) => {
    console.log("[PDF] exportToPDF chamado", {
      hasElement: !!elementRef.current,
    });

    if (!elementRef.current) {
      toast({
        title: "Erro ao exportar",
        description: "Elemento não encontrado para exportação.",
        variant: "destructive",
      });
      return;
    }

    console.log("[PDF] Iniciando exportação...");
    setIsExporting(true);

    const element = elementRef.current;
    
    console.log("[PDF] Dimensões iniciais do elemento:", {
      offsetWidth: element.offsetWidth,
      offsetHeight: element.offsetHeight,
      scrollWidth: element.scrollWidth,
      scrollHeight: element.scrollHeight,
      childCount: element.childElementCount,
    });

    if (element.scrollHeight === 0 || element.offsetHeight === 0) {
      console.warn("[PDF] AVISO: Elemento com altura 0. Conteúdo pode não ter renderizado ainda.");
    }
    
    // Salvar estilos originais
    const originalStyles = {
      opacity: element.style.opacity,
      visibility: element.style.visibility,
      position: element.style.position,
      zIndex: element.style.zIndex,
      top: element.style.top,
      left: element.style.left
    };

    try {
      // Tornar elemento visível temporariamente para captura
      console.log("[PDF] Tornando elemento visível para captura...");
      element.style.opacity = '1';
      element.style.visibility = 'visible';
      element.style.position = 'fixed';
      element.style.zIndex = '9999';
      element.style.top = '0';
      element.style.left = '0';
      
      // Aguardar render com elemento visível (AUMENTADO PARA 2s)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      console.log("[PDF] Antes de prepareSVGsForPDF:", {
        offsetWidth: element.offsetWidth,
        offsetHeight: element.offsetHeight,
        scrollWidth: element.scrollWidth,
        scrollHeight: element.scrollHeight,
      });
      
      // Preparar SVGs antes da exportação
      prepareSVGsForPDF(element);
      
      // DIMENSÕES FIXAS ABSOLUTAS PARA A4
      const windowWidth = 794;  // 210mm em pixels (96dpi)
      const windowHeight = 1123; // 297mm em pixels (96dpi)

      console.log("[PDF] html2canvas com dimensões FIXAS A4:", {
        windowWidth,
        windowHeight,
      });
      
      console.log("[PDF] Iniciando html2pdf...");
      const opt = {
        margin: [10, 10, 10, 10] as [number, number, number, number],
        filename: customFilename || defaultFilename,
        image: { type: "jpeg" as const, quality: 0.95 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: 794,
          windowHeight: 1123
        },
        jsPDF: { 
          unit: "mm" as const, 
          format: "a4" as const, 
          orientation: "portrait" as const
        },
        pagebreak: { mode: ['css'] }
      };

      await html2pdf().set(opt).from(element).save();
      
      console.log("[PDF] html2pdf finalizado, aparentemente sem erros.");
      console.log("[PDF] Exportação concluída com sucesso!");

      toast({
        title: "PDF gerado com sucesso!",
        description: `Arquivo ${opt.filename} foi baixado.`,
      });
    } catch (error) {
      console.error("[PDF] Erro ao gerar PDF:", error);
      toast({
        title: "Erro ao gerar PDF",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      // Restaurar estilos originais
      console.log("[PDF] Restaurando estilos originais...");
      Object.assign(element.style, originalStyles);
      setIsExporting(false);
    }
  };

  return { exportToPDF, isExporting };
}
